import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import ContactInfoDrawer from '../ContactInfoDrawer';
import { Contact, ContactType, getContactTypes, listContacts } from '../../services/contacts.service';

/**
 * Contacts subtable for a table-maintenance drawer — the reusable
 * ContactInfoDrawer keyed by (tablename, tablerecno). The drawer self-saves;
 * this panel just lists + opens it. Shared by Bill Tos, Customers, etc.
 */
const ContactsPanel: React.FC<{ tablename: string; recno: number }> = ({ tablename, recno }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [types, setTypes] = useState<ContactType[]>([]);
  const [drawer, setDrawer] = useState<{ mode: 'create' | 'edit'; contactRecno: number | null } | null>(null);

  const typeName = useMemo(() => {
    const m = new Map(types.map((t) => [t.recno, t.type]));
    return (id: number) => m.get(id) || '';
  }, [types]);

  const load = useCallback(() => {
    listContacts(tablename, recno).then(setContacts).catch(() => {});
  }, [tablename, recno]);

  const loadTypes = useCallback(() => {
    getContactTypes().then(setTypes).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    loadTypes();
  }, [load, loadTypes]);

  // Re-pull the type list every time the contact drawer opens. Contact types are
  // user-maintained (Data Management → Contact Types); a list fetched once at
  // mount hid types added since, and the Type dropdown needed a page reload.
  useEffect(() => {
    if (drawer) loadTypes();
  }, [drawer, loadTypes]);

  return (
    <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">Contacts</legend>

      {contacts.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No contacts yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted">
              <th className="py-1 font-medium">Type</th>
              <th className="py-1 font-medium">Name</th>
              <th className="py-1 font-medium">Title</th>
              <th className="py-1 font-medium">Email</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.recno} className="text-gray-800 dark:text-gray-200">
                <td className="py-1">{typeName(c.contacttype)}</td>
                <td className="py-1">{c.contactname}</td>
                <td className="py-1">{c.contacttitle}</td>
                <td className="py-1">{c.contactemail}</td>
                <td className="py-1 text-right">
                  <button
                    type="button"
                    onClick={() => setDrawer({ mode: 'edit', contactRecno: c.recno })}
                    className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title="Edit contact"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button
        type="button"
        onClick={() => setDrawer({ mode: 'create', contactRecno: null })}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
      >
        <PlusIcon className="w-4 h-4" /> Add Contact
      </button>

      {drawer && (
        <ContactInfoDrawer
          isOpen
          mode={drawer.mode}
          contactRecno={drawer.contactRecno}
          tablename={tablename}
          tablerecno={recno}
          contactTypes={types}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            load();
          }}
        />
      )}
    </fieldset>
  );
};

export default ContactsPanel;
